import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Volume2, Captions, Square, Loader2, Pencil, Check } from "lucide-react";
import { mediaUrl, fetchTtsBlob } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";

const POS = { center: "object-center", top: "object-top", bottom: "object-bottom" };

const RATIO_CLASS = { "9:16": "aspect-[9/16] w-[250px]", "1:1": "aspect-square w-[320px]", "16:9": "aspect-video w-[420px]" };
const RATIO_COMPACT = { "9:16": "aspect-[9/16] w-[170px]", "1:1": "aspect-square w-[220px]", "16:9": "aspect-video w-[300px]" };

/** Animated storyboard preview: cycles through scenes with on-screen text, subtitles and CTA. */
export function VideoPreview({ project, onPatch, compact = false }) {
  const scenes = useMemo(() => project?.scenes || [], [project?.scenes]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [voice, setVoice] = useState("idle"); // idle | loading | playing
  const [editVoice, setEditVoice] = useState(false);
  const [voiceText, setVoiceText] = useState(project?.voiceover_script || "");
  const audioRef = useRef(null);

  useEffect(() => { setVoiceText(project?.voiceover_script || ""); }, [project?.voiceover_script]);
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const stopVoice = () => { audioRef.current?.pause(); audioRef.current = null; setVoice("idle"); };
  const playVoice = async () => {
    if (voice !== "idle") return stopVoice();
    try {
      setVoice("loading");
      const blob = await fetchTtsBlob(voiceText || project.voiceover_script);
      const a = new Audio(URL.createObjectURL(blob));
      audioRef.current = a;
      a.onended = () => setVoice("idle");
      a.onerror = () => setVoice("idle");
      setVoice("playing");
      setIdx(0); setPlaying(true);
      await a.play();
    } catch { setVoice("idle"); }
  };

  useEffect(() => { setIdx(0); }, [project?.id, scenes.length]);

  useEffect(() => {
    if (!playing || scenes.length === 0) return undefined;
    const dur = (scenes[idx]?.duration || 3) * 1000;
    const t = setTimeout(() => setIdx((i) => (i + 1) % scenes.length), dur);
    return () => clearTimeout(t);
  }, [idx, playing, scenes]);

  if (!project) return null;
  const s = scenes[idx] || {};
  const isLast = idx === scenes.length - 1;
  const bg = mediaUrl(s.media_url || project.poster_url);
  const isVideo = s.media_kind === "video";
  const pos = POS[s.crop] || "object-center";
  const total = scenes.reduce((a, x) => a + (x.duration || 3), 0);

  return (
    <div className="flex flex-col items-center gap-3" data-testid="video-preview">
      <div className={`relative ${(compact ? RATIO_COMPACT : RATIO_CLASS)[project.ratio] || RATIO_CLASS["9:16"]} max-w-full rounded-[28px] overflow-hidden border border-white/10 bg-[#050912] shadow-[0_0_60px_rgba(34,211,238,0.12)]`}>
        {bg && isVideo && (
          <video key={bg + idx} src={bg} muted autoPlay loop playsInline className={`absolute inset-0 w-full h-full object-cover ${pos}`} />
        )}
        {bg && !isVideo && (
          <img key={bg + idx} src={bg} alt="" className={`absolute inset-0 w-full h-full object-cover ${pos} scale-105 animate-[kenburns_6s_ease-in-out_infinite_alternate]`} />
        )}
        {s.media_source === "ai" && (
          <span className="absolute top-7 right-3 px-1.5 py-0.5 rounded bg-violet-500/80 text-[9px] uppercase tracking-wider font-bold text-white">AI</span>
        )}
        {s.media_source === "user" && (
          <span className="absolute top-7 right-3 px-1.5 py-0.5 rounded bg-cyan-400/90 text-[9px] uppercase tracking-wider font-bold text-slate-950">Tuo file</span>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/85" />
        <div className="absolute top-3 left-3 right-3 flex gap-1">
          {scenes.map((_, i) => (
            <span key={i} className={`h-[3px] flex-1 rounded-full ${i <= idx ? "bg-white" : "bg-white/25"}`} />
          ))}
        </div>
        <div className="absolute top-7 left-3 text-[9px] uppercase tracking-widest font-bold text-white/70">
          {project.format_label} · {project.ratio} · {total}s
        </div>
        <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 text-center">
          <div key={idx} className="font-heading font-extrabold text-white text-2xl leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)] step-enter" data-testid="preview-on-screen-text">
            {s.on_screen_text}
          </div>
        </div>
        <div className="absolute inset-x-3 bottom-3 space-y-2">
          {isLast && project.cta && (
            <div className="mx-auto w-fit px-4 py-2 rounded-full bg-white text-slate-950 text-xs font-bold tracking-wide step-enter" data-testid="preview-cta">
              {project.cta}
            </div>
          )}
          {s.voiceover && project.subtitles_enabled !== false && (
            <div className="rounded-lg bg-black/60 backdrop-blur px-2.5 py-1.5 text-[11px] text-white text-center leading-snug" data-testid="preview-subtitle">
              {s.voiceover}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-400">
        <button onClick={() => setPlaying((p) => !p)} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-slate-200 hover:border-cyan-400/50 hover:text-cyan-200" data-testid="preview-play-toggle">
          {playing ? <Pause size={13} /> : <Play size={13} />}
        </button>
        <span>Scena {idx + 1}/{scenes.length}</span>
        <span className={`inline-flex items-center gap-1 ${project.voiceover_enabled === false ? "text-slate-600 line-through" : "text-slate-500"}`}><Volume2 size={11} /> voce Nadia</span>
        <span className={`inline-flex items-center gap-1 ${project.subtitles_enabled === false ? "text-slate-600 line-through" : "text-slate-500"}`}><Captions size={11} /> sottotitoli</span>
      </div>
      {!compact && <div className="flex gap-1.5 flex-wrap justify-center">
        {scenes.map((sc, i) => (
          <button
            key={i}
            onClick={() => { setIdx(i); setPlaying(false); }}
            className={`w-10 h-10 rounded-lg overflow-hidden border ${i === idx ? "border-cyan-400" : "border-white/10 opacity-60 hover:opacity-100"}`}
            data-testid={`preview-scene-${i + 1}`}
          >
            {sc.media_url ? (sc.media_kind === "video" ? <video src={mediaUrl(sc.media_url)} muted className="w-full h-full object-cover" /> : <img src={mediaUrl(sc.media_url)} alt="" className="w-full h-full object-cover" />) : <span className="w-full h-full flex items-center justify-center text-[9px] text-slate-400 bg-white/[0.04]">CTA</span>}
          </button>
        ))}
      </div>}

      {project.voiceover_enabled !== false && (
        <div className={`w-full ${compact ? "max-w-[300px]" : "max-w-[420px]"} rounded-2xl border border-white/10 bg-white/[0.02] p-3`} data-testid="voice-block">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold inline-flex items-center gap-1"><Volume2 size={11} /> Voce Nadia</span>
            <div className="flex items-center gap-1.5">
              {!compact && onPatch && (
                editVoice ? (
                  <button onClick={async () => { await onPatch({ voiceover_script: voiceText }); setEditVoice(false); }} className="h-7 px-2 rounded-md border border-emerald-500/40 text-emerald-300 text-xs inline-flex items-center gap-1 hover:bg-emerald-500/10" data-testid="voice-save">
                    <Check size={11} /> Salva
                  </button>
                ) : (
                  <button onClick={() => setEditVoice(true)} className="h-7 px-2 rounded-md border border-white/10 text-slate-300 text-xs inline-flex items-center gap-1 hover:text-cyan-200 hover:border-cyan-400/40" data-testid="voice-edit">
                    <Pencil size={11} /> Modifica testo
                  </button>
                )
              )}
              <button onClick={playVoice} className={`h-7 px-2.5 rounded-md text-xs font-semibold inline-flex items-center gap-1 ${voice === "idle" ? "bg-cyan-500 text-slate-950 hover:bg-cyan-400" : "bg-rose-500/80 text-white hover:bg-rose-500"}`} data-testid="voice-play">
                {voice === "loading" ? <Loader2 size={11} className="animate-spin" /> : voice === "playing" ? <Square size={11} /> : <Play size={11} />}
                {voice === "idle" ? "Ascolta voce" : voice === "loading" ? "Preparo…" : "Stop"}
              </button>
            </div>
          </div>
          {compact ? null : editVoice ? (
            <Textarea value={voiceText} onChange={(e) => setVoiceText(e.target.value)} rows={4} className="mt-2 bg-[#0B1120] border-white/10 text-slate-100 text-xs" data-testid="voice-text" />
          ) : (
            <p className="mt-2 text-xs text-slate-300 leading-relaxed" data-testid="voice-script">{voiceText || project.voiceover_script}</p>
          )}
        </div>
      )}
    </div>
  );
}
